import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { numberToWords } from '../utils/formatCurrency';
import { useLocation } from 'react-router-dom';
import { salesInvoiceService, productService } from '../services/api';
import InvoicePreview from '../components/InvoicePreview';
import Footer from '../components/Footer';
import ChangeHistory from '../components/ChangeHistory';
import Select from '../components/Select';
import SearchInput from '../components/SearchInput';
import { useInvoice } from '../context/InvoiceContext';
import { useConfirm } from '../context/ConfirmContext';

const formatINR = (n) => '\u20B9' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getProductSummary = (inv) => {
  const items = inv.items;
  if (!Array.isArray(items) || items.length === 0) return '-';
  return items.map(it => it.description).filter(Boolean).join(', ') || '-';
};

const installmentAmount = (inst, grandTotal) => inst.amountOverride !== undefined && inst.amountOverride !== ''
  ? parseFloat(inst.amountOverride)
  : grandTotal * (parseFloat(inst.percent) || 0) / 100;

const getMatchingInstallments = (inv, filters) => {
  const schedule = inv.formData?.paymentSchedule;
  if (!schedule || schedule.length === 0 || !filters?.month) return [];
  return schedule.filter(s => s.dueDate
    && (!filters.year || getFY(s.dueDate) === filters.year)
    && s.dueDate.slice(5, 7) === filters.month);
};

const getDisplayDueDate = (inv, filters) => {
  const matches = getMatchingInstallments(inv, filters);
  if (matches.length > 0) return matches[0].dueDate;
  return inv.dueDate;
};

// The postponement record for whichever due date is currently displayed —
// only returned when it was actually postponed (differs from the current
// due date) — so admins can see how much leeway has already been given,
// and how many times, before deciding whether to grant more.
const getDisplayPostponement = (inv, filters) => {
  const matches = getMatchingInstallments(inv, filters);
  const schedule = inv.formData?.paymentSchedule;
  let inst;
  if (matches.length > 0) inst = matches[0];
  else if (schedule && schedule.length > 0) inst = schedule.find(s => s.dueDate === inv.dueDate) || schedule[schedule.length - 1];
  else inst = inv.formData;
  if (!inst?.originalDueDate || inst.originalDueDate === inst.dueDate) return null;
  return inst;
};

// Records a due-date change on an already-saved invoice: keeps the full
// sequence of prior dates (with when each change happened), not just the
// original, so admins can see the count and pattern of postponements —
// not only that one happened. No-ops while still drafting a brand-new
// invoice, where every date is provisional until the first save.
const recordDueDateChange = (obj, newDate, isEditingExisting) => {
  if (!isEditingExisting) return { ...obj, dueDate: newDate };
  let history = obj.dueDateHistory || [];
  if (history.length === 0 && obj.originalDueDate && obj.originalDueDate !== obj.dueDate) {
    history = [{ date: obj.originalDueDate, changedAt: null }];
  }
  history = [...history, { date: obj.dueDate, changedAt: new Date().toISOString() }];
  return { ...obj, dueDate: newDate, originalDueDate: history[0].date, dueDateHistory: history };
};

const fmtShortDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// "was <original>" note shown next to a postponed due date, with a count
// and a hover tooltip listing the full sequence of prior dates and when
// each change was made.
const PostponementNote = ({ obj, size = '11px', onClear }) => {
  if (!obj?.originalDueDate || obj.originalDueDate === obj.dueDate) return null;
  const history = obj.dueDateHistory && obj.dueDateHistory.length > 0
    ? obj.dueDateHistory
    : [{ date: obj.originalDueDate, changedAt: null }];
  const tooltip = [...history.map(h => fmtShortDate(h.date)), fmtShortDate(obj.dueDate)].join(' → ') + '\n\n'
    + history.filter(h => h.changedAt).map(h => `Changed ${fmtShortDate(h.changedAt)}`).join('\n');
  return (
    <div style={{ fontSize: size, color: '#B45309', marginTop: '2px' }} title={tooltip}>
      was {fmtShortDate(obj.originalDueDate)}{history.length > 1 && ` · postponed ${history.length}×`}
      {onClear && (
        <button type="button" onClick={onClear} title="Remove this note"
          style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', padding: '0 2px', marginLeft: '4px', fontSize: 'inherit', lineHeight: 1, fontWeight: 600 }}>
          &times;
        </button>
      )}
    </div>
  );
};

// True if any part of this invoice is still owed and its due date has
// already passed "today" — used so an overdue balance from an earlier
// month doesn't disappear once you're no longer viewing that month.
const isUnpaidOverdue = (inv, todayStr) => {
  if (inv.status === 'cancelled' || inv.status === 'paid') return false;
  const schedule = inv.formData?.paymentSchedule;
  if (schedule && schedule.length > 0) {
    return schedule.some(s => !s.paid && !s.waived && s.dueDate && s.dueDate < todayStr);
  }
  return !!inv.dueDate && inv.dueDate < todayStr;
};

// Amount due for whichever due date is currently displayed (selected month's installment, or the fallback due date)
const computeSelectedDueAmount = (inv, filters) => {
  const grandTotal = parseFloat(inv.grandTotal) || 0;
  if (inv.status === 'cancelled' || inv.status === 'paid') return 0;
  const schedule = inv.formData?.paymentSchedule;
  if (schedule && schedule.length > 0) {
    const matches = getMatchingInstallments(inv, filters);
    if (matches.length > 0) {
      return matches.filter(s => !s.paid && !s.waived).reduce((sum, s) => sum + installmentAmount(s, grandTotal), 0);
    }
    const fallback = schedule.find(s => s.dueDate === inv.dueDate) || schedule[schedule.length - 1];
    return (fallback.paid || fallback.waived) ? 0 : installmentAmount(fallback, grandTotal);
  }
  return grandTotal;
};

const STATUS_META = {
  due:            { label: 'DUE',            bg: '#FEF3C7', color: '#92400E' },
  partially_paid: { label: 'PARTIALLY PAID', bg: '#DBEAFE', color: '#1E40AF' },
  paid:           { label: 'PAID',           bg: '#D1FAE5', color: '#065F46' },
  cancelled:      { label: 'CANCELLED',      bg: '#FEE2E2', color: '#991B1B' },
  waived:         { label: 'WAIVED',         bg: '#F3F4F6', color: '#4B5563' },
};

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const InvStatusBadge = ({ invoiceId, status, schedule, onUpdate, readOnly }) => {
  const [open, setOpen] = useState(false);
  const [dropStyle, setDropStyle] = useState({});
  const ref = React.useRef(null);
  const btnRef = React.useRef(null);
  const confirm = useConfirm();

  const hasSchedule = schedule && schedule.length > 0;
  const paidCount = hasSchedule ? schedule.filter(s => s.paid).length : 0;
  const waivedCount = hasSchedule ? schedule.filter(s => s.waived).length : 0;
  const hasWaivable = hasSchedule && schedule.some(s => !s.paid && !s.waived);
  const meta = STATUS_META[status] || STATUS_META.due;

  const badgeLabel = (() => {
    if (status === 'cancelled') return 'CANCELLED';
    if (status === 'paid') return 'PAID';
    if (hasSchedule && paidCount > 0) {
      return waivedCount > 0 ? `${paidCount}/${schedule.length} PAID · ${waivedCount} WAIVED` : `${paidCount}/${schedule.length} PAID`;
    }
    return meta.label;
  })();

  // Build dropdown options
  const options = hasSchedule
    ? [
        { key: 'due', label: 'Due', meta: STATUS_META.due },
        ...schedule.map((_, i) => ({
          key: `inst_${i}`,
          label: `${ordinal(i + 1)} installment paid`,
          meta: STATUS_META.partially_paid,
          installmentIndex: i,
        })),
        ...(hasWaivable ? [{ key: 'waive_remaining', label: 'Waive remaining installment(s)', meta: STATUS_META.waived }] : []),
        ...(waivedCount > 0 ? [{ key: 'unwaive', label: 'Un-waive installment(s)', meta: STATUS_META.due }] : []),
        { key: 'cancelled', label: 'Cancelled', meta: STATUS_META.cancelled },
      ]
    : Object.entries(STATUS_META).filter(([key]) => !['partially_paid', 'waived'].includes(key)).map(([key, m]) => ({ key, label: m.label, meta: m }));

  const isActive = (opt) => {
    if (hasSchedule && opt.installmentIndex !== undefined) return paidCount === opt.installmentIndex + 1 && status === 'partially_paid';
    return opt.key === status;
  };

  React.useEffect(() => {
    if (!open) return;
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropStyle({
        position: 'fixed',
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
        minWidth: '140px',
        zIndex: 9999,
        ...(spaceBelow < 160
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    }
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const handleSelect = async (opt) => {
    setOpen(false);
    if (opt.key === 'waive_remaining') {
      const ok = await confirm(
        'Waive the remaining unpaid installment(s)? Use this when someone has discontinued and the rest will never be collected — it clears the due amount without counting the waived portion as revenue.',
        { title: 'Waive Remaining Installments', confirmLabel: 'Waive' }
      );
      if (!ok) return;
      const newSchedule = schedule.map(inst => (inst.paid || inst.waived) ? inst : { ...inst, waived: true });
      const stillPaidCount = schedule.filter(s => s.paid).length;
      await salesInvoiceService.updateStatus(invoiceId, stillPaidCount > 0 ? 'partially_paid' : 'cancelled', { paymentSchedule: newSchedule });
    } else if (opt.key === 'unwaive') {
      const newSchedule = schedule.map(inst => inst.waived ? { ...inst, waived: false } : inst);
      const paidNow = schedule.filter(s => s.paid).length;
      await salesInvoiceService.updateStatus(invoiceId, paidNow > 0 ? 'partially_paid' : 'due', { paymentSchedule: newSchedule });
    } else if (opt.installmentIndex !== undefined) {
      const newSchedule = schedule.map((inst, i) => ({ ...inst, paid: i <= opt.installmentIndex, waived: i <= opt.installmentIndex ? false : inst.waived }));
      const allPaid = opt.installmentIndex === schedule.length - 1;
      await salesInvoiceService.updateStatus(invoiceId, allPaid ? 'paid' : 'partially_paid', { paymentSchedule: newSchedule });
    } else {
      if (opt.key === status) return;
      if (opt.key === 'due' && hasSchedule) {
        const newSchedule = schedule.map(inst => ({ ...inst, paid: false, waived: false }));
        await salesInvoiceService.updateStatus(invoiceId, 'due', { paymentSchedule: newSchedule });
      } else {
        await salesInvoiceService.updateStatus(invoiceId, opt.key);
      }
    }
    onUpdate();
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={btnRef}
        onClick={() => !readOnly && setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          padding: '3px 8px', fontSize: '11px', fontWeight: 600,
          background: meta.bg, color: meta.color, border: `1px solid ${meta.color}33`,
          borderRadius: '2px', cursor: readOnly ? 'default' : 'pointer', textTransform: 'uppercase',
          letterSpacing: '0.04em', lineHeight: 1.4, whiteSpace: 'nowrap',
        }}
      >
        {badgeLabel}
        {!readOnly && <svg width="7" height="4" viewBox="0 0 7 4" style={{ opacity: 0.6 }}><path d="M0 0l3.5 4L7 0z" fill="currentColor" /></svg>}
      </button>
      {open && !readOnly && (
        <div style={{
          ...dropStyle, background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '4px',
        }}>
          {options.map((opt) => (
            <button
              key={opt.key}
              onClick={() => handleSelect(opt)}
              style={{
                display: 'block', width: '100%', padding: '5px 8px', border: 'none',
                background: isActive(opt) ? opt.meta.bg : 'transparent', color: opt.meta.color,
                fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.04em', cursor: 'pointer', borderRadius: '2px',
                textAlign: 'left', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { if (!isActive(opt)) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { if (!isActive(opt)) e.currentTarget.style.background = 'transparent'; }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
// Same day-of-month N months later, clamped to the target month's last day
// (31 Jan + 1mo → 28/29 Feb). Built from local parts to avoid TZ drift.
const addMonths = (dateStr, months) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const target = new Date(y, m - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-${String(target.getDate()).padStart(2, '0')}`;
};

const FALLBACK_PRESET_ITEMS = [];

const defaultItem = () => ({ description: '', qty: '1', rate: '', gst: '18' });

const getFY = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  const y = d.getFullYear();
  const m = d.getMonth(); // 0-indexed
  // Indian FY: Apr–Mar. If Jan–Mar, FY started previous year.
  const startYear = m < 3 ? y - 1 : y;
  return `${String(startYear).slice(2)}-${String(startYear + 1).slice(2)}`;
};

const makeInvNo = (fy, num) => `INV/${fy}/${String(num).padStart(6, '0')}`;

const defaultForm = () => ({
  invoiceNumber: '',
  companyName: 'Neuberlinr Private Limited',
  companyAddress: 'No.47, Bharathi Street,\nTiruchirappalli - 620005,\nTamil Nadu',
  companyCIN: 'CIN: U70200TN2026PTC188018',
  companyGSTIN: 'GSTIN: 33AALCN1785B1ZL',
  companyState: 'Tamil Nadu',
  companyStateCode: '33',
  companyPhone: '+91 73389 90669',
  companyEmail: 'madurai@eurocas.org',
  companyWebsite: 'www.eurocas.org',
  billTo: '',
  billToGSTIN: '',
  placeOfSupply: 'Tamil Nadu (33)',
  supplyType: 'intra', // 'intra' = CGST+SGST, 'inter' = IGST
  mode: '', // 'online' or 'office'
  reverseCharge: false,
  invoiceDate: today(),
  terms: 'Due on Receipt',
  dueDate: today(),
  items: [defaultItem()],
  discount: '',
  accountName: 'NEUBERLINR PRIVATE LIMITED',
  accountNumber: '5020 0118 4521 33',
  bankName: 'HDFC Bank',
  ifsc: 'HDFC0001177',
  branch: 'CHETTIKULAM JUNCTION',
  signatureMode: 'digital', // 'digital' or 'physical'
  paymentSchedule: [], // [{ percent: 50, dueDate: '2026-03-17', label: '' }]
});

const GenerateInvoice = () => {
  const { allInvoices, currentUser, hasPermission, showNotification } = useInvoice();
  const confirm = useConfirm();
  const canCreateInvoice = hasPermission('create_invoice');
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const location = useLocation();
  const leadFromNav = location.state?.lead;
  const [leadId] = useState(leadFromNav?.id || null);
  const [form, setForm] = useState(() => {
    if (leadFromNav) {
      const f = defaultForm();
      const billParts = [leadFromNav.name, leadFromNav.phone, leadFromNav.email].filter(Boolean);
      f.billTo = billParts.join('\n');
      return f;
    }
    try {
      const saved = sessionStorage.getItem('invoiceForm');
      if (saved) return { ...defaultForm(), ...JSON.parse(saved) };
    } catch {}
    return defaultForm();
  });
  const [editingFrom, setEditingFrom] = useState(false);
  const [editingAccount, setEditingAccount] = useState(false);
  const [editingInvNo, setEditingInvNo] = useState(false);
  const [showPreview, setShowPreview] = useState(() => window.location.hash === '#preview');
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState(leadFromNav && isSuperAdmin ? 'form' : 'list');

  const currentFY = getFY();
  const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
  const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [invFilters, setInvFilters] = useState({ search: '', year: '', month: currentMonth, monthMode: 'all', startDate: '', endDate: '' });
  const [invSort, setInvSort] = useState({ column: 'dueDate', direction: 'ASC' });
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState(false);
  const [invHistory, setInvHistory] = useState([]);
  const [invHistoryLoading, setInvHistoryLoading] = useState(false);
  const [showInvHistory, setShowInvHistory] = useState(false);
  const previewRef = useRef(null);
  const dlPreviewRef = useRef(null);
  const [dlInvoice, setDlInvoice] = useState(null);
  const [PRESET_ITEMS, setPresetItems] = useState(FALLBACK_PRESET_ITEMS);
  const [installmentCount, setInstallmentCount] = useState(2); // min 2

  // Load invoice history when viewing
  useEffect(() => {
    if (!viewingInvoice) { setInvHistory([]); setShowInvHistory(false); return; }
    setInvHistoryLoading(true);
    salesInvoiceService.history(viewingInvoice.id).then(res => setInvHistory(res.data || [])).catch(() => {}).finally(() => setInvHistoryLoading(false));
  }, [viewingInvoice]);

  // Load products from DB
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await productService.list();
        const products = res.data || [];
        if (products.length > 0) {
          setPresetItems(products.filter(p => p.active !== false).map(p => ({ label: p.name, rate: String(p.rate), gst: parseFloat(p.gst) })));
        }
      } catch {}
    };
    loadProducts();
  }, []);

  // Load next invoice number from DB on mount
  const loadInvoices = useCallback(async () => {
    try {
      const res = await salesInvoiceService.list();
      setSavedInvoices(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const handleDeleteInvoice = useCallback(async (inv) => {
    const ok = await confirm(`Delete invoice ${inv.invoiceNumber}? This cannot be undone.`, {
      title: 'Delete Invoice',
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await salesInvoiceService.delete(inv.id);
      await loadInvoices();
      showNotification('Invoice deleted', 'success');
    } catch (e) {
      showNotification(e.message || 'Failed to delete invoice', 'error');
    }
  }, [confirm, loadInvoices, showNotification]);

  useEffect(() => {
    const loadInvNo = async () => {
      if (form.invoiceNumber) return;
      try {
        const res = await salesInvoiceService.getNextNumber();
        setForm(f => ({ ...f, invoiceNumber: res.invoiceNumber }));
      } catch {
        setForm(f => ({ ...f, invoiceNumber: makeInvNo(getFY(), 1) }));
      }
    };
    loadInvNo();
    loadInvoices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.location.hash = showPreview ? '#preview' : '';
  }, [showPreview]);

  useEffect(() => {
    sessionStorage.setItem('invoiceForm', JSON.stringify(form));
  }, [form]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const setItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, items };
    });
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, defaultItem()] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const itemSubtotal = (it) => (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0);
  const itemGst = (it) => { const g = parseFloat(it.gst) || 0; return itemSubtotal(it) * g / 100; };
  const itemTotal = (it) => itemSubtotal(it) + itemGst(it);
  const subtotal = form.items.reduce((s, it) => s + itemSubtotal(it), 0);
  const totalGst = form.items.reduce((s, it) => s + itemGst(it), 0);
  const discount = Math.max(0, Math.min(parseFloat(form.discount) || 0, subtotal));
  const grandTotal = subtotal - discount + totalGst;
  const isIntra = form.supplyType === 'intra';
  const totalCgst = isIntra ? totalGst / 2 : 0;
  const totalSgst = isIntra ? totalGst / 2 : 0;
  const totalIgst = isIntra ? 0 : totalGst;

  const canDownload = form.billTo.trim() && form.items.some(it => it.description.trim() && (parseFloat(it.rate) || 0) > 0);

  const isInvoiceNumberTaken = savedInvoices.some(i => i.invoiceNumber === form.invoiceNumber && i.id !== editingInvoiceId);
  // Editing an existing invoice can renumber onto a taken number — the server
  // cascades everything in between to make room. Only block for a brand-new
  // invoice (incl. duplicating), where there's no cascade, just a collision.
  const blockNumberCollision = isInvoiceNumberTaken && !editingInvoiceId;

  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset to a blank form with a fresh invoice number, discarding any leftover
  // state from a previously-viewed/edited invoice (e.g. switching to "New Invoice"
  // right after editing one, without saving/cancelling first).
  const resetToNewInvoice = useCallback(async () => {
    sessionStorage.removeItem('invoiceForm');
    const f = defaultForm();
    try {
      const res = await salesInvoiceService.getNextNumber();
      f.invoiceNumber = res.invoiceNumber;
    } catch { f.invoiceNumber = makeInvNo(getFY(), 1); }
    setForm(f);
  }, []);

  const saveInvoice = useCallback(async () => {
    if (!canDownload || (!editingInvoiceId && !duplicateMode)) return;
    if (blockNumberCollision) {
      showNotification('This invoice number is already in use.', 'error');
      return;
    }
    setSaving(true);
    try {
      const billToFirstLine = (form.billTo || '').split('\n')[0].trim();
      const payload = {
        invoiceNumber: form.invoiceNumber,
        customerName: billToFirstLine,
        customerGSTIN: form.billToGSTIN || null,
        placeOfSupply: form.placeOfSupply,
        supplyType: form.supplyType,
        mode: form.mode || null,
        invoiceDate: form.invoiceDate,
        dueDate: form.dueDate,
        terms: form.terms,
        subtotal, totalCgst, totalSgst, totalIgst, grandTotal,
        items: form.items,
        formData: form,
      };
      if (editingInvoiceId) {
        await salesInvoiceService.update(editingInvoiceId, payload);
      } else {
        await salesInvoiceService.create(payload);
      }
      await loadInvoices();
      setEditingInvoiceId(null);
      setDuplicateMode(false);
      setShowPreview(false);
    } catch (e) {
      console.error('Failed to save invoice:', e);
      showNotification(e.message || 'Failed to save invoice', 'error');
    } finally {
      setSaving(false);
    }
  }, [canDownload, editingInvoiceId, duplicateMode, blockNumberCollision, form, subtotal, totalCgst, totalSgst, totalIgst, grandTotal, loadInvoices, showNotification]);

  const downloadExistingInvoice = useCallback(async (inv) => {
    if (!inv.invoiceNumber) {
      showNotification('Assign an invoice number before downloading this one', 'error');
      return;
    }
    setDlInvoice(inv);
    // Wait for React to render the hidden preview
    await new Promise(r => setTimeout(r, 200));
    const el = dlPreviewRef.current;
    if (!el) return;
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff', width: el.offsetWidth, height: el.offsetHeight });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
      pdf.save(inv.invoiceNumber.replace(/\//g, '-') + '.pdf');
    } catch (e) {
      console.error('PDF generation failed:', e);
    } finally {
      setDlInvoice(null);
    }
  }, [showNotification]);

  const downloadPDF = useCallback(async () => {
    if (!canDownload || !previewRef.current) return;
    setGenerating(true);

    // Re-check the true next number right before finalizing a NEW invoice, computed live
    // from actual invoice rows (not a separately-tracked counter that can drift out of sync
    // whenever an invoice gets created/edited/renumbered through a different path). The
    // number shown in the form was only computed once when this tab last loaded, which can
    // go stale if the tab sat open while other invoices were created elsewhere in the
    // meantime — leading to a duplicate/out-of-sequence number.
    let invoiceNumberToUse = form.invoiceNumber;
    if (!editingInvoiceId) {
      try {
        const res = await salesInvoiceService.getNextNumber(form.invoiceDate);
        // Adopt the fresh number only when ours is stale/behind. A form number
        // *ahead* of the server's suggestion is deliberate (Skip parks the lower
        // number for the next invoice) and must not be clobbered here.
        const fyOf = n => (n || '').split('/')[1] || '';
        const seqOf = n => parseInt((n || '').split('/').pop(), 10) || 0;
        if (fyOf(res.invoiceNumber) !== fyOf(invoiceNumberToUse) || seqOf(res.invoiceNumber) > seqOf(invoiceNumberToUse)) {
          invoiceNumberToUse = res.invoiceNumber;
          setForm(f => ({ ...f, invoiceNumber: res.invoiceNumber }));
          await new Promise(r => setTimeout(r, 50)); // let the preview re-render with the fresh number
        }
      } catch (e) { /* fall back to whatever was already shown; the server self-corrects on save */ }
    }

    try {
      const el = previewRef.current;
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: el.offsetWidth,
        height: el.offsetHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = 210;
      const pdfH = 297;
      const imgW = pdfW;
      const imgH = (canvas.height * pdfW) / canvas.width;

      if (imgH <= pdfH + 1) {
        pdf.addImage(imgData, 'JPEG', 0, 0, imgW, imgH);
      } else {
        // Multi-page: slice the canvas
        let yOffset = 0;
        while (yOffset < canvas.height) {
          const pageCanvas = document.createElement('canvas');
          const sliceH = Math.min(canvas.height - yOffset, (canvas.width * pdfH) / pdfW);
          pageCanvas.width = canvas.width;
          pageCanvas.height = sliceH;
          const ctx = pageCanvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, sliceH);
          ctx.drawImage(canvas, 0, yOffset, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
          const pageImg = pageCanvas.toDataURL('image/jpeg', 0.95);
          const pageImgH = (sliceH * pdfW) / canvas.width;
          if (yOffset > 0) pdf.addPage();
          pdf.addImage(pageImg, 'JPEG', 0, 0, imgW, pageImgH);
          yOffset += sliceH;
        }
      }

      const filename = invoiceNumberToUse.replace(/\//g, '-') + '.pdf';
      pdf.save(filename);

      // Save invoice record to DB (create or update)
      try {
        const billToFirstLine = (form.billTo || '').split('\n')[0].trim();
        const payload = {
          invoiceNumber: invoiceNumberToUse,
          customerName: billToFirstLine,
          customerGSTIN: form.billToGSTIN || null,
          placeOfSupply: form.placeOfSupply,
          supplyType: form.supplyType,
          mode: form.mode || null,
          invoiceDate: form.invoiceDate,
          dueDate: form.dueDate,
          terms: form.terms,
          subtotal, totalCgst, totalSgst, totalIgst, grandTotal,
          items: form.items,
          formData: { ...form, invoiceNumber: invoiceNumberToUse },
          leadId: leadId || null,
        };
        // If we're editing a specific invoice, update it even if its number just changed.
        // Otherwise fall back to matching by number (e.g. re-downloading without an explicit edit).
        const existing = editingInvoiceId
          ? savedInvoices.find(i => i.id === editingInvoiceId)
          : savedInvoices.find(i => i.invoiceNumber === invoiceNumberToUse);
        if (existing) {
          await salesInvoiceService.update(existing.id, payload);
        } else {
          await salesInvoiceService.create(payload);
        }
        await loadInvoices();
        if (editingInvoiceId) {
          setEditingInvoiceId(null);
          setShowPreview(false);
        }
      } catch (e) {
        console.error('Failed to save invoice record:', e);
        showNotification(e.message || 'Failed to save invoice', 'error');
      }
    } catch (e) {
      console.error('PDF generation failed:', e);
    } finally {
      setGenerating(false);
    }
  }, [canDownload, form, subtotal, totalCgst, totalSgst, totalIgst, grandTotal, loadInvoices, savedInvoices, editingInvoiceId, leadId, showNotification]);

  const invYearOptions = useMemo(() => {
    const fys = [...new Set(savedInvoices.map(i => i.invoiceDate && getFY(i.invoiceDate)).filter(Boolean))];
    return fys.sort((a, b) => b.localeCompare(a));
  }, [savedInvoices]);

  const invAvailableMonths = useMemo(() => {
    const set = new Set();
    const inYear = (dateStr) => !invFilters.year || (dateStr && getFY(dateStr) === invFilters.year);
    savedInvoices.forEach(i => {
      if (i.invoiceDate && inYear(i.invoiceDate)) set.add(i.invoiceDate.slice(5, 7));
      // Also offer months where an installment is due, even if no invoice
      // was created that month — e.g. a future installment due next month.
      (i.formData?.paymentSchedule || []).forEach(s => {
        if (s.dueDate && inYear(s.dueDate)) set.add(s.dueDate.slice(5, 7));
      });
    });
    if (!invFilters.year || invFilters.year === currentFY) set.add(currentMonth);
    return set;
  }, [savedInvoices, invFilters.year, currentFY, currentMonth]);

  const invHasActiveFilters = invFilters.search || invFilters.startDate || invFilters.endDate
    || invFilters.year || invFilters.month;

  const clearInvFilters = () => setInvFilters({ search: '', year: '', month: '', monthMode: 'all', startDate: '', endDate: '' });

  const handleInvSort = (col) => {
    setInvSort(s => s.column === col
      ? { column: col, direction: s.direction === 'ASC' ? 'DESC' : 'ASC' }
      : { column: col, direction: 'DESC' });
  };
  const getInvSortIndicator = (col) => invSort.column !== col ? null : (invSort.direction === 'ASC' ? ' ↑' : ' ↓');

  const filteredInvoices = useMemo(() => {
    let list = [...savedInvoices];
    const { search, year, month, startDate, endDate } = invFilters;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.invoiceNumber?.toLowerCase().includes(q) ||
        i.customerName?.toLowerCase().includes(q) ||
        String(i.grandTotal || '').includes(q) ||
        getProductSummary(i).toLowerCase().includes(q)
      );
    }
    if (year) list = list.filter(i => {
      if (i.invoiceDate && getFY(i.invoiceDate) === year) return true;
      return (i.formData?.paymentSchedule || []).some(s => s.dueDate && getFY(s.dueDate) === year);
    });
    if (month) list = list.filter(i => {
      const mode = invFilters.monthMode || 'all';
      // "Created": strictly invoices dated in the selected month
      if (mode === 'created') return i.invoiceDate?.slice(5, 7) === month;
      // "Due": strictly the invoice's currently-effective due date (the same
      // one shown in the Due Date column) falling in the selected month —
      // no invoice-date check, no overdue carry-over.
      if (mode === 'due') {
        const d = getDisplayDueDate(i, invFilters);
        return d?.slice(5, 7) === month;
      }
      // "All": the original combined view — invoice date, any installment
      // due, or (for the current month) an unpaid balance overdue from earlier.
      if (i.invoiceDate?.slice(5, 7) === month) return true;
      if ((i.formData?.paymentSchedule || []).some(s => s.dueDate?.slice(5, 7) === month)) return true;
      if (month === currentMonth && isUnpaidOverdue(i, today())) return true;
      return false;
    });
    if (startDate) list = list.filter(i => i.invoiceDate >= startDate);
    if (endDate) list = list.filter(i => i.invoiceDate <= endDate);

    const { column, direction } = invSort;
    list.sort((a, b) => {
      let av, bv;
      if (column === 'grandTotal') {
        av = parseFloat(a[column] || 0); bv = parseFloat(b[column] || 0);
      } else if (column === 'dueDate') {
        av = getDisplayDueDate(a, invFilters) || ''; bv = getDisplayDueDate(b, invFilters) || '';
      } else {
        av = a[column] || ''; bv = b[column] || '';
      }
      if (av < bv) return direction === 'ASC' ? -1 : 1;
      if (av > bv) return direction === 'ASC' ? 1 : -1;
      return 0;
    });
    return list;
  }, [savedInvoices, invFilters, invSort, currentMonth]);

  const pendingTotal = filteredInvoices.reduce((sum, inv) => sum + computeSelectedDueAmount(inv, invFilters), 0);

  const receivedBalance = filteredInvoices.reduce((acc, inv) => {
    if (inv.status === 'cancelled') return acc;
    const sub = parseFloat(inv.subtotal || 0);
    const gst = parseFloat(inv.totalCgst || 0) + parseFloat(inv.totalSgst || 0) + parseFloat(inv.totalIgst || 0);
    const schedule = inv.formData?.paymentSchedule || [];
    const { year, month } = invFilters;
    const instInFilter = (s) => {
      if (year && (!s.dueDate || getFY(s.dueDate) !== year)) return false;
      if (month && s.dueDate?.slice(5, 7) !== month) return false;
      return true;
    };
    if (inv.status === 'paid') {
      if (schedule.length > 0) {
        const pct = schedule.filter(instInFilter).reduce((s, p) => s + parseFloat(p.percent || 0), 0) / 100;
        acc.sub += sub * pct; acc.gst += gst * pct;
      } else {
        acc.sub += sub; acc.gst += gst;
      }
    } else if (inv.status === 'partially_paid') {
      const pct = schedule.filter(s => s.paid && instInFilter(s)).reduce((s, p) => s + parseFloat(p.percent || 0), 0) / 100;
      acc.sub += sub * pct; acc.gst += gst * pct;
    }
    return acc;
  }, { sub: 0, gst: 0 });

  return (
    <>
      <div className="main-content">
        <div className="container">
          {/* Header */}
          <div className="flex-between mb-lg">
            <div>
              <h1 style={{ marginBottom: '4px' }}>Invoices</h1>
              {(activeTab === 'form' || editingInvoiceId || duplicateMode) && (form.invoiceNumber || editingInvoiceId) && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    {editingInvNo ? (
                      <>
                        <input
                          className="form-input"
                          value={form.invoiceNumber || ''}
                          onChange={e => set('invoiceNumber', e.target.value)}
                          style={{
                            width: '150px', fontSize: 'var(--font-size-sm)', padding: '3px 8px',
                            borderColor: blockNumberCollision ? '#DC2626' : undefined,
                          }}
                        />
                        <button onClick={() => !blockNumberCollision && setEditingInvNo(false)} className="btn btn-secondary btn-sm" style={{ padding: '3px 10px', fontSize: '12px' }} disabled={blockNumberCollision}>Done</button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: form.invoiceNumber ? 'var(--color-text-secondary)' : '#B45309', fontWeight: 'var(--font-weight-medium)', fontStyle: form.invoiceNumber ? undefined : 'italic' }}>
                          {form.invoiceNumber || 'No invoice number'}
                          {editingInvoiceId && <span style={{ color: 'var(--color-primary)', marginLeft: '6px', fontSize: '11px' }}>(editing)</span>}
                          {duplicateMode && <span style={{ color: 'var(--color-primary)', marginLeft: '6px', fontSize: '11px' }}>(duplicate)</span>}
                        </span>
                        {editingInvoiceId && form.invoiceNumber && (
                          <div title="Move to a different number. Whatever's between the old and new position shifts by one to make room — nothing collides." style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                            <button
                              onClick={() => {
                                const m = /^INV\/(\d{2}-\d{2})\/(\d+)$/.exec(form.invoiceNumber);
                                if (!m) return;
                                const next = parseInt(m[2], 10) + 1;
                                set('invoiceNumber', `INV/${m[1]}/${String(next).padStart(6, '0')}`);
                              }}
                              style={{ border: 'none', borderBottom: '1px solid var(--color-gray-200)', background: 'var(--color-gray-50)', padding: '0 6px', fontSize: '9px', lineHeight: '13px', cursor: 'pointer' }}
                            >▲</button>
                            <button
                              onClick={() => {
                                const m = /^INV\/(\d{2}-\d{2})\/(\d+)$/.exec(form.invoiceNumber);
                                if (!m) return;
                                const next = Math.max(1, parseInt(m[2], 10) - 1);
                                set('invoiceNumber', `INV/${m[1]}/${String(next).padStart(6, '0')}`);
                              }}
                              style={{ border: 'none', background: 'var(--color-gray-50)', padding: '0 6px', fontSize: '9px', lineHeight: '13px', cursor: 'pointer' }}
                            >▼</button>
                          </div>
                        )}
                        <button onClick={() => setEditingInvNo(true)} className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: '11px' }}>{form.invoiceNumber ? 'Edit' : 'Assign'}</button>
                        {form.invoiceNumber && (
                          <button
                            title={editingInvoiceId
                              ? 'Unassign this number — the invoice stays as-is, just without a number. Every later invoice compacts down one number, and the number freed at the top is reserved for the next new invoice.'
                              : 'Reserve this number for the next invoice and move this one to the number after it'}
                            onClick={async () => {
                              const skipped = form.invoiceNumber;
                              try {
                                if (editingInvoiceId) {
                                  const ok = await confirm(`Skip ${skipped}? This invoice keeps everything else and stays where it is, just without a number. Every later invoice moves down one number to close the gap, and the number freed at the top is reserved for the next new invoice.`, {
                                    title: 'Skip Invoice Number',
                                    confirmLabel: 'Skip'
                                  });
                                  if (!ok) return;
                                  const res = await salesInvoiceService.skipExistingNumber(editingInvoiceId);
                                  set('invoiceNumber', '');
                                  showNotification(res.cascaded > 0
                                    ? `${skipped} unassigned — ${res.cascaded} later invoice${res.cascaded !== 1 ? 's' : ''} moved down; ${res.reservedNumber} is reserved for the next new invoice`
                                    : `${skipped} unassigned and reserved for the next new invoice`, 'success');
                                  await loadInvoices();
                                } else {
                                  const res = await salesInvoiceService.skipNumber(skipped);
                                  set('invoiceNumber', res.invoiceNumber);
                                  showNotification(`${skipped} skipped — the next invoice will use it`, 'success');
                                }
                              } catch (e) {
                                showNotification(e.message || 'Failed to skip invoice number', 'error');
                              }
                            }}
                            className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: '11px' }}
                          >
                            Skip
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {editingInvNo && blockNumberCollision && (
                    <div style={{ color: '#DC2626', fontSize: '11px', marginTop: '4px' }}>This invoice number is already in use.</div>
                  )}
                  {isInvoiceNumberTaken && editingInvoiceId && (
                    <div style={{ color: '#B45309', fontSize: '11px', marginTop: '4px' }}>
                      Taken by another invoice — saving will shift invoices between the old and new position to make room.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
              {(() => {
                const totalExpenses = allInvoices
                  .filter(i => {
                    if (invFilters.year && !i.invoiceDate?.startsWith(invFilters.year)) return false;
                    if (invFilters.month && i.invoiceDate?.slice(5, 7) !== invFilters.month) return false;
                    return true;
                  })
                  .reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
                return totalExpenses > 0 ? (
                  <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: '8px', padding: '8px 16px', textAlign: 'right' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#991B1B', lineHeight: 1.2 }}>{formatINR(totalExpenses)}</div>
                    <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Office Expenses</div>
                  </div>
                ) : null;
              })()}
              {receivedBalance.sub + receivedBalance.gst > 0 && (
                <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: '8px', padding: '8px 16px', textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#065F46', lineHeight: 1.2 }}>{formatINR(receivedBalance.sub + receivedBalance.gst)}</div>
                  <div style={{ fontSize: '11px', color: '#047857', marginTop: '2px' }}>{formatINR(receivedBalance.sub)} + GST {formatINR(receivedBalance.gst)}</div>
                </div>
              )}
              {pendingTotal > 0 && (
                <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', padding: '8px 16px', textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#92400E', lineHeight: 1.2 }}>{formatINR(pendingTotal)}</div>
                  <div style={{ fontSize: '11px', color: '#B45309', marginTop: '2px' }}>Pending Payment</div>
                </div>
              )}
              {(receivedBalance.sub + receivedBalance.gst + pendingTotal) > 0 && (
                <div style={{ background: '#FFEDD5', border: '1px solid #FDBA74', borderRadius: '8px', padding: '8px 16px', textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#9A3412', lineHeight: 1.2 }}>{formatINR(receivedBalance.sub + receivedBalance.gst + pendingTotal)}</div>
                  <div style={{ fontSize: '11px', color: '#C2410C', marginTop: '2px' }}>Total Collectable</div>
                </div>
              )}
              {activeTab === 'form' && !editingInvoiceId && canDownload && (
                <button onClick={() => setShowPreview(p => !p)} className="btn btn-secondary">
                  {showPreview ? 'Edit' : 'Preview'}
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '2px', marginBottom: 'var(--spacing-lg)', borderBottom: '2px solid var(--color-gray-200)' }}>
            <button
              onClick={() => { setActiveTab('list'); setShowPreview(false); setViewingInvoice(null); setEditingInvoiceId(null); setDuplicateMode(false); }}
              style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'list' ? 'var(--font-weight-semibold)' : 'normal', color: activeTab === 'list' ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: activeTab === 'list' ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: 'var(--font-size-sm)', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              Invoices
              {savedInvoices.length > 0 && (
                <span style={{ background: 'var(--color-gray-200)', borderRadius: '2px', padding: '1px 7px', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)' }}>
                  {savedInvoices.length}
                </span>
              )}
            </button>
            {canCreateInvoice && <button
              onClick={() => {
                // Discard leftover form state from a previously edited/cancelled invoice —
                // recognizable by its number still matching a saved invoice — so "New Invoice"
                // never silently reuses another invoice's number and details.
                const formHoldsSavedInvoice = editingInvoiceId || duplicateMode || savedInvoices.some(i => i.invoiceNumber === form.invoiceNumber);
                if (formHoldsSavedInvoice) resetToNewInvoice();
                setActiveTab('form'); setViewingInvoice(null); setEditingInvoiceId(null); setDuplicateMode(false);
              }}
              style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'form' ? 'var(--font-weight-semibold)' : 'normal', color: activeTab === 'form' ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: activeTab === 'form' ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: 'var(--font-size-sm)' }}
            >
              New Invoice
            </button>}
          </div>

          {/* Invoices List Tab */}
          {activeTab === 'list' && !viewingInvoice && !editingInvoiceId && !duplicateMode && (
            savedInvoices.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📄</div>
                <div className="empty-state-text">No invoices generated yet</div>
                {canCreateInvoice && <button onClick={() => setActiveTab('form')} className="btn btn-primary mt-md">Create First Invoice</button>}
              </div>
            ) : (
              <>
              {/* Filters */}
              <div className="card mb-lg">
                <div className="flex gap-md" style={{ flexWrap: 'wrap', alignItems: 'stretch' }}>
                  <div style={{ flex: '1 1 260px', display: 'flex' }}>
                    <SearchInput
                      placeholder="Search invoice no, customer, product, amount..."
                      value={invFilters.search}
                      onChange={v => setInvFilters(f => ({ ...f, search: v }))}
                      inputStyle={{ height: '100%' }}
                    />
                  </div>
                  <div style={{ flex: '0 1 auto', display: 'flex' }}>
                    <Select
                      value={invFilters.year}
                      onChange={v => setInvFilters(f => ({ ...f, year: v, month: '' }))}
                      options={[{ value: '', label: 'All FYs' }, ...invYearOptions.map(y => ({ value: y, label: `FY ${y}` }))]}
                      style={{ minWidth: '110px' }}
                    />
                  </div>
                  <div style={{ flex: '0 1 auto', display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', padding: '0 var(--spacing-xs)' }}>
                    <input type="date" style={{ border: 'none', padding: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}
                      value={invFilters.startDate} onChange={e => setInvFilters(f => ({ ...f, startDate: e.target.value }))} />
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>to</span>
                    <input type="date" style={{ border: 'none', padding: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}
                      value={invFilters.endDate} onChange={e => setInvFilters(f => ({ ...f, endDate: e.target.value }))} />
                  </div>
                  <button onClick={clearInvFilters} className="btn btn-secondary btn-sm" disabled={!invHasActiveFilters} style={{ alignSelf: 'stretch' }}>
                    Clear Filters
                  </button>
                </div>
                {invAvailableMonths.size > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-xs)', marginTop: 'var(--spacing-md)', alignItems: 'center' }}>
                    <div style={{ display: 'flex', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginRight: 'var(--spacing-sm)' }}
                      title="All: invoice date, due date, or overdue carry-over (original view). Due: strictly the due date shown in the table. Created: strictly the invoice date.">
                      {[['all', 'All'], ['due', 'Due'], ['created', 'Created']].map(([mode, label]) => (
                        <button key={mode} onClick={() => setInvFilters(f => ({ ...f, monthMode: mode }))}
                          className="month-chip"
                          style={{
                            border: 'none', borderRadius: 0,
                            background: (invFilters.monthMode || 'all') === mode ? 'var(--color-primary)' : 'transparent',
                            color: (invFilters.monthMode || 'all') === mode ? '#fff' : undefined,
                          }}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setInvFilters(f => ({ ...f, month: '' }))} className={`month-chip${!invFilters.month ? ' month-chip-active' : ''}`}>All</button>
                    {MONTH_LABELS.map((label, i) => {
                      const val = String(i + 1).padStart(2, '0');
                      if (!invAvailableMonths.has(val)) return null;
                      return (
                        <button key={val} onClick={() => setInvFilters(f => ({ ...f, month: val }))} className={`month-chip${invFilters.month === val ? ' month-chip-active' : ''}`}>{label}</button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex-between mb-md" style={{ alignItems: 'center' }}>
                <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                  {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="card" style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                      <th onClick={() => handleInvSort('invoiceNumber')} style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Invoice No{getInvSortIndicator('invoiceNumber')}</th>
                      <th onClick={() => handleInvSort('mode')} style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Mode{getInvSortIndicator('mode')}</th>
                      <th onClick={() => handleInvSort('customerName')} style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Customer{getInvSortIndicator('customerName')}</th>
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Product</th>
                      <th onClick={() => handleInvSort('invoiceDate')} style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Invoice Date{getInvSortIndicator('invoiceDate')}</th>
                      <th onClick={() => handleInvSort('grandTotal')} style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Total Amount{getInvSortIndicator('grandTotal')}</th>
                      <th onClick={() => handleInvSort('dueDate')} style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Due Date{getInvSortIndicator('dueDate')}</th>
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Due Amount</th>
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Status</th>
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map(inv => {
                      const amountDue = computeSelectedDueAmount(inv, invFilters);
                      const isFullyPaid = amountDue <= 0;
                      const displayDueDate = getDisplayDueDate(inv, invFilters);
                      const isOverdue = !isFullyPaid && displayDueDate && displayDueDate < today();
                      const isDueSoon = !isFullyPaid && !isOverdue && displayDueDate && displayDueDate <= addDays(today(), 5);
                      const rowBg = inv.status === 'cancelled' ? '#F3E9DC' : isFullyPaid ? '#ECFDF5' : isOverdue ? '#FEF2F2' : isDueSoon ? '#FEF9C3' : undefined;
                      return (
                        <tr key={inv.id} style={{ borderBottom: '1px solid var(--color-gray-100)', cursor: 'pointer', background: rowBg }} onClick={() => setViewingInvoice(inv)}>
                          <td style={{ padding: 'var(--spacing-sm)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
                            {inv.invoiceNumber || <span style={{ color: '#B45309', fontStyle: 'italic', fontWeight: 'var(--font-weight-medium)' }}>No number</span>}
                          </td>
                          <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {inv.mode && (
                              <span style={{
                                fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '2px',
                                color: inv.mode === 'online' ? '#0369A1' : '#9A3412',
                                background: inv.mode === 'online' ? '#E0F2FE' : '#FFEDD5',
                              }}>
                                {inv.mode === 'online' ? 'Online' : 'Office'}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={inv.customerName}>{inv.customerName}</td>
                          <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getProductSummary(inv)}>{getProductSummary(inv)}</td>
                          <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{new Date(inv.invoiceDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>{formatINR(inv.grandTotal)}</td>
                          <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>
                            {(() => {
                              const d = getDisplayDueDate(inv, invFilters);
                              const postponement = getDisplayPostponement(inv, invFilters);
                              return (
                                <>
                                  {d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                  {postponement && <PostponementNote obj={postponement} size="10px" />}
                                </>
                              );
                            })()}
                          </td>
                          <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', color: computeSelectedDueAmount(inv, invFilters) > 0 ? '#B91C1C' : 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{formatINR(computeSelectedDueAmount(inv, invFilters))}</td>
                          <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                            <InvStatusBadge invoiceId={inv.id} status={inv.status} schedule={inv.formData?.paymentSchedule} onUpdate={loadInvoices} readOnly={false} />
                          </td>
                          <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => downloadExistingInvoice(inv)}
                                className="btn btn-secondary btn-sm"
                                style={{ fontSize: '11px', padding: '2px 10px' }}
                              >
                                Download
                              </button>
                              <button
                                onClick={() => {
                                  const loaded = { ...defaultForm(), ...inv.formData, invoiceNumber: inv.invoiceNumber, mode: inv.mode || '' };
                                  if (loaded.paymentSchedule && loaded.paymentSchedule.length > 0) {
                                    loaded.terms = 'As per Payment Schedule';
                                    const lastDate = loaded.paymentSchedule.reduce((max, p) => p.dueDate > max ? p.dueDate : max, loaded.paymentSchedule[0].dueDate);
                                    loaded.dueDate = lastDate;
                                  }
                                  setForm(loaded);
                                  setViewingInvoice(null);
                                  setEditingInvoiceId(inv.id);
                                  setShowPreview(false);
                                }}
                                className="btn btn-secondary btn-sm"
                                style={{ fontSize: '11px', padding: '2px 10px' }}
                              >
                                Edit
                              </button>
                              {inv.invoiceNumber && (
                                <button
                                  title="Unassign this invoice's number — it stays as-is, just without a number. Every later invoice compacts down one number, and the number freed at the top is reserved for the next new invoice."
                                  onClick={async () => {
                                    const ok = await confirm(`Skip ${inv.invoiceNumber}? This invoice keeps everything else and stays where it is, just without a number. Every later invoice moves down one number to close the gap, and the number freed at the top is reserved for the next new invoice.`, {
                                      title: 'Skip Invoice Number',
                                      confirmLabel: 'Skip'
                                    });
                                    if (!ok) return;
                                    try {
                                      const oldNo = inv.invoiceNumber;
                                      const res = await salesInvoiceService.skipExistingNumber(inv.id);
                                      showNotification(res.cascaded > 0
                                        ? `${oldNo} unassigned — ${res.cascaded} later invoice${res.cascaded !== 1 ? 's' : ''} moved down; ${res.reservedNumber} is reserved for the next new invoice`
                                        : `${oldNo} unassigned and reserved for the next new invoice`, 'success');
                                      await loadInvoices();
                                    } catch (e) {
                                      showNotification(e.message || 'Failed to skip invoice number', 'error');
                                    }
                                  }}
                                  className="btn btn-secondary btn-sm"
                                  style={{ fontSize: '11px', padding: '2px 10px' }}
                                >
                                  Skip
                                </button>
                              )}
                              <button
                                onClick={async () => {
                                  const loaded = {
                                    ...defaultForm(), ...inv.formData,
                                    invoiceDate: today(),
                                    dueDate: today(),
                                    terms: 'Due on Receipt',
                                    paymentSchedule: [],
                                  };
                                  try {
                                    const res = await salesInvoiceService.getNextNumber(loaded.invoiceDate);
                                    loaded.invoiceNumber = res.invoiceNumber;
                                  } catch { loaded.invoiceNumber = makeInvNo(getFY(), 1); }
                                  setForm(loaded);
                                  setViewingInvoice(null);
                                  setEditingInvoiceId(null);
                                  setDuplicateMode(true);
                                  setShowPreview(false);
                                }}
                                className="btn btn-secondary btn-sm"
                                style={{ fontSize: '11px', padding: '2px 10px' }}
                              >
                                Duplicate
                              </button>
                              {isSuperAdmin && (
                                <button
                                  onClick={() => handleDeleteInvoice(inv)}
                                  className="btn btn-secondary btn-sm"
                                  style={{ fontSize: '11px', padding: '2px 10px', color: '#DC2626' }}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )
          )}

          {/* View saved invoice */}
          {activeTab === 'list' && viewingInvoice && !editingInvoiceId && (
            <>
              <div style={{ marginBottom: 'var(--spacing-md)', display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button onClick={() => setViewingInvoice(null)} className="btn btn-secondary btn-sm">Back to list</button>
                <button onClick={() => downloadExistingInvoice(viewingInvoice)} className="btn btn-primary btn-sm">Download</button>
              </div>
              <div className="mb-md" style={{ overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', borderRadius: 'var(--radius-md)', background: '#f5f5f5', padding: 'var(--spacing-md)', display: 'flex', justifyContent: 'center' }}>
                <InvoicePreview
                  form={viewingInvoice.formData}
                  subtotal={parseFloat(viewingInvoice.subtotal)}
                  discount={parseFloat(viewingInvoice.formData?.discount) || 0}
                  totalCgst={parseFloat(viewingInvoice.totalCgst)}
                  totalSgst={parseFloat(viewingInvoice.totalSgst)}
                  totalIgst={parseFloat(viewingInvoice.totalIgst)}
                  grandTotal={parseFloat(viewingInvoice.grandTotal)}
                  itemSubtotal={itemSubtotal}
                  itemGst={itemGst}
                  itemTotal={itemTotal}
                  internal
                />
              </div>
              <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                <button type="button" onClick={() => setShowInvHistory(!showInvHistory)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: 'var(--color-primary)', padding: 0 }}>
                  {showInvHistory ? '▾ Hide History' : '▸ Show History'} ({invHistory.length})
                </button>
                {showInvHistory && (
                  <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                    <ChangeHistory history={invHistory} loading={invHistoryLoading} />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
                <button onClick={() => setViewingInvoice(null)} className="btn btn-secondary">Back to list</button>
                <button onClick={() => downloadExistingInvoice(viewingInvoice)} className="btn btn-primary">Download</button>
              </div>
            </>
          )}

          {/* Edit/duplicate existing invoice (inline in list tab) */}
          {activeTab === 'list' && (editingInvoiceId || duplicateMode) && (
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <button onClick={() => { setEditingInvoiceId(null); setDuplicateMode(false); }} className="btn btn-secondary btn-sm" style={{ marginBottom: 'var(--spacing-md)' }}>Back to list</button>
            </div>
          )}

          {/* Invoice Form / Preview — shown for New Invoice tab OR when editing/duplicating from list */}
          {(activeTab === 'form' || (activeTab === 'list' && (editingInvoiceId || duplicateMode))) && <>

          {/* Preview mode */}
          {showPreview && canDownload ? (
            <>
              <div className="card mb-md" style={{ padding: 0, overflow: 'hidden' }}>
                <InvoicePreview
                  ref={previewRef}
                  form={form}
                  subtotal={subtotal}
                  discount={discount}
                  totalCgst={totalCgst}
                  totalSgst={totalSgst}
                  totalIgst={totalIgst}
                  grandTotal={grandTotal}
                  itemSubtotal={itemSubtotal}
                  itemGst={itemGst}
                  itemTotal={itemTotal}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
                <button onClick={() => setShowPreview(false)} className="btn btn-secondary">
                  Back to Edit
                </button>
                <button onClick={downloadPDF} disabled={generating} className="btn btn-primary">
                  {generating ? 'Generating...' : 'Generate & Download'}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* From + Bill To (side by side) */}
              <div className="card mb-md">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-lg)' }}>
                  {/* From */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="card-header">From</div>
                      <button onClick={() => setEditingFrom(e => !e)} className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: '11px' }}>
                        {editingFrom ? 'Done' : 'Edit'}
                      </button>
                    </div>
                    {editingFrom ? (
                      <>
                        <div className="form-group">
                          <label className="form-label">Company Name</label>
                          <input className="form-input" value={form.companyName} onChange={e => set('companyName', e.target.value)} placeholder="Your Company PVT Ltd" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Address</label>
                          <textarea className="form-input" style={{ minHeight: '60px', resize: 'vertical' }} value={form.companyAddress} onChange={e => set('companyAddress', e.target.value)} placeholder={"Street, City, State, PIN\nCountry"} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-sm)' }}>
                          <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.companyPhone} onChange={e => set('companyPhone', e.target.value)} placeholder="+91 ..." /></div>
                          <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={form.companyEmail} onChange={e => set('companyEmail', e.target.value)} placeholder="info@company.com" /></div>
                          <div className="form-group"><label className="form-label">Website</label><input className="form-input" value={form.companyWebsite} onChange={e => set('companyWebsite', e.target.value)} placeholder="www.company.com" /></div>
                          <div className="form-group"><label className="form-label">CIN</label><input className="form-input" value={form.companyCIN} onChange={e => set('companyCIN', e.target.value)} placeholder="CIN: ..." /></div>
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">GSTIN</label><input className="form-input" value={form.companyGSTIN} onChange={e => set('companyGSTIN', e.target.value)} placeholder="GSTIN: ..." /></div>
                      </>
                    ) : (
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                        <div style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-base)' }}>{form.companyName}</div>
                        {form.companyAddress && <>
                          <div style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)', marginTop: 'var(--spacing-xs)' }}>Head Office:</div>
                          <div style={{ whiteSpace: 'pre-line' }}>{form.companyAddress}</div>
                        </>}
                        {form.companyPhone && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Ph:</span> {form.companyPhone}</div>}
                        {form.companyEmail && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Email:</span> {form.companyEmail}</div>}
                        {form.companyWebsite && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Website:</span> {form.companyWebsite}</div>}
                        {form.companyCIN && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>CIN:</span> {form.companyCIN.replace(/^CIN:\s*/i, '')}</div>}
                        {form.companyGSTIN && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>GSTIN:</span> {form.companyGSTIN.replace(/^GSTIN:\s*/i, '')}</div>}
                      </div>
                    )}
                  </div>

                  {/* Bill To */}
                  <div>
                    <div className="card-header">Bill To <span style={{ color: '#DC2626', fontSize: 'var(--font-size-xs)' }}>*</span></div>
                    <textarea className="form-input" style={{ minHeight: '130px', resize: 'vertical', borderColor: !form.billTo.trim() ? '#DC2626' : undefined, boxShadow: !form.billTo.trim() ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} value={form.billTo} onChange={e => set('billTo', e.target.value)} placeholder={"Client name\nAddress\nCity, State, PIN"} />
                    <div className="form-group" style={{ marginTop: 'var(--spacing-sm)', marginBottom: 0 }}>
                      <label className="form-label">Recipient GSTIN <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>(if registered)</span></label>
                      <input className="form-input" value={form.billToGSTIN} onChange={e => set('billToGSTIN', e.target.value)} placeholder="Optional — for B2B invoices" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="card mb-md">
                <div className="card-header">Invoice Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--spacing-md)' }}>
                  <div className="form-group">
                    <label className="form-label">Invoice Date</label>
                    <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                      <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => setForm(f => { const d = addDays(f.invoiceDate, -1); return { ...f, invoiceDate: d, dueDate: f.terms === 'Due on Receipt' ? d : addDays(d, 2) }; })}>-</button>
                      <input type="date" className="form-input" style={{ flex: 1 }} value={form.invoiceDate} onChange={e => { const d = e.target.value; setForm(f => ({ ...f, invoiceDate: d, dueDate: f.terms === 'Due on Receipt' ? d : addDays(d, 2) })); }} />
                      <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => setForm(f => { const d = addDays(f.invoiceDate, 1); return { ...f, invoiceDate: d, dueDate: f.terms === 'Due on Receipt' ? d : addDays(d, 2) }; })}>+</button>
                    </div>
                  </div>
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                    <label className="form-label">Terms</label>
                    <input className="form-input" value={form.terms} onChange={e => set('terms', e.target.value)} style={{ flex: 1 }} disabled={form.paymentSchedule.length > 0} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Due Date</label>
                    {form.paymentSchedule.length > 0 ? (
                      <input type="date" className="form-input" value={form.dueDate} disabled style={{ opacity: 0.6 }} />
                    ) : (
                      <div>
                        <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                          <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => set('dueDate', addDays(form.dueDate, -1))}>-</button>
                          <input type="date" className="form-input" style={{ flex: 1 }} value={form.dueDate} onChange={e => {
                            setForm(f => recordDueDateChange(f, e.target.value, !!editingInvoiceId));
                          }} />
                          <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => set('dueDate', addDays(form.dueDate, 1))}>+</button>
                        </div>
                        <PostponementNote obj={form} onClear={() => {
                          setForm(f => { const { originalDueDate, dueDateHistory, ...rest } = f; return rest; });
                        }} />
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--spacing-md)' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Place of Supply</label>
                    <input className="form-input" value={form.placeOfSupply} onChange={e => set('placeOfSupply', e.target.value)} placeholder="State Name (Code)" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Supply Type</label>
                    <select className="form-select" value={form.supplyType} onChange={e => set('supplyType', e.target.value)}>
                      <option value="intra">Intra-State (CGST + SGST)</option>
                      <option value="inter">Inter-State (IGST)</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Reverse Charge</label>
                    <select className="form-select" value={form.reverseCharge ? 'yes' : 'no'} onChange={e => set('reverseCharge', e.target.value === 'yes')}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Invoice Type</label>
                    <select className="form-select" value={form.signatureMode} onChange={e => set('signatureMode', e.target.value)}>
                      <option value="digital">Digital</option>
                      <option value="physical">Physical (with signature)</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Mode</label>
                    <select className="form-select" value={form.mode} onChange={e => set('mode', e.target.value)}>
                      <option value="">— Mode —</option>
                      <option value="online">Online</option>
                      <option value="office">Office</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="card mb-md">
                <div className="card-header">Items <span style={{ color: '#DC2626', fontSize: 'var(--font-size-xs)' }}>*</span></div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>Description</th>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '80px' }}>Qty</th>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '100px' }}>Rate</th>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '70px' }}>GST %</th>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '100px' }}>Taxable</th>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '100px' }}>Total</th>
                        <th style={{ width: '40px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((item, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                            <select
                              className="form-select"
                              value={PRESET_ITEMS.some(p => p.label === item.description) ? item.description : '__custom'}
                              onChange={e => {
                                const val = e.target.value;
                                if (val === '__custom') {
                                  setItem(i, 'description', '');
                                } else {
                                  const preset = PRESET_ITEMS.find(p => p.label === val);
                                  setForm(f => {
                                    const items = [...f.items];
                                    items[i] = { ...items[i], description: val, rate: preset ? preset.rate : items[i].rate, gst: preset ? String(preset.gst) : items[i].gst };
                                    return { ...f, items };
                                  });
                                }
                              }}
                              style={{ marginBottom: 'var(--spacing-xs)', borderColor: !item.description.trim() ? '#DC2626' : undefined, boxShadow: !item.description.trim() ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }}
                            >
                              {PRESET_ITEMS.map(p => (<option key={p.label} value={p.label}>{p.label}</option>))}
                              <option value="__custom">Custom...</option>
                            </select>
                            {!PRESET_ITEMS.some(p => p.label === item.description) && (
                              <input className="form-input" value={item.description} onChange={e => setItem(i, 'description', e.target.value)} placeholder="Item description" style={{ borderColor: !item.description.trim() ? '#DC2626' : undefined, boxShadow: !item.description.trim() ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} />
                            )}
                          </td>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                            <input className="form-input no-spinner" type="number" min="0" step="0.01" value={item.qty} onChange={e => setItem(i, 'qty', e.target.value)} style={{ textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                            <input className="form-input no-spinner" type="number" min="0" step="0.01" value={item.rate} onChange={e => setItem(i, 'rate', e.target.value)} style={{ textAlign: 'right', borderColor: !(parseFloat(item.rate) > 0) ? '#DC2626' : undefined, boxShadow: !(parseFloat(item.rate) > 0) ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} />
                          </td>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                            <input className="form-input no-spinner" type="number" min="0" step="1" value={item.gst} onChange={e => setItem(i, 'gst', e.target.value)} placeholder="0" style={{ textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                            {formatINR(itemSubtotal(item))}
                          </td>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)' }}>
                            {formatINR(itemTotal(item))}
                          </td>
                          <td style={{ padding: 'var(--spacing-xs)', textAlign: 'center' }}>
                            {form.items.length > 1 && (
                              <button onClick={() => removeItem(i)} className="btn btn-secondary btn-sm" style={{ padding: '4px 8px', fontSize: '14px', lineHeight: 1 }}>&times;</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: 'var(--spacing-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <button onClick={addItem} className="btn btn-secondary btn-sm">+ Add Item</button>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Taxable Value: {formatINR(subtotal)}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--spacing-xs)', marginTop: '2px' }}>
                      <label htmlFor="invoice-discount" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Discount:</label>
                      <input
                        id="invoice-discount"
                        className="form-input no-spinner"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.discount}
                        onChange={e => set('discount', e.target.value)}
                        placeholder="0"
                        style={{ width: '100px', textAlign: 'right' }}
                      />
                    </div>
                    {discount > 0 && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>Taxable Value After Discount: {formatINR(subtotal - discount)}</div>}
                    {isIntra && totalCgst > 0 && <>
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>CGST: {formatINR(totalCgst)}</div>
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>SGST: {formatINR(totalSgst)}</div>
                    </>}
                    {!isIntra && totalIgst > 0 && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>IGST: {formatINR(totalIgst)}</div>}
                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', marginTop: '2px' }}>Total: {formatINR(grandTotal)}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontStyle: 'italic', marginTop: '2px' }}>{numberToWords(grandTotal)}</div>
                  </div>
                </div>
              </div>

              {/* Payment Schedule */}
              {(() => {
                const splitEqually = (n, baseDate) => {
                  const base = parseFloat(n) || 2;
                  const perPct = Math.floor(10000 / base) / 100; // e.g. 3 → 33.33
                  const lastPct = parseFloat((100 - perPct * (base - 1)).toFixed(2));
                  return Array.from({ length: base }, (_, i) => ({
                    label: `Installment ${i + 1}`,
                    percent: String(i === base - 1 ? lastPct : perPct),
                    dueDate: addDays(baseDate, 30 * i),
                    paid: false,
                  }));
                };
                return (
                  <div className="card mb-md">
                    <div className="card-header" style={{ borderBottom: form.paymentSchedule.length > 0 ? undefined : 'none', marginBottom: form.paymentSchedule.length > 0 ? undefined : 0, paddingBottom: form.paymentSchedule.length > 0 ? undefined : 0 }}>Payment Schedule</div>
                    {form.paymentSchedule.length === 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-sm)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                          <button type="button" onClick={() => setInstallmentCount(c => Math.max(2, c - 1))} className="btn btn-secondary" style={{ border: 'none', borderRadius: 0, padding: '4px 10px', fontSize: '16px', lineHeight: 1 }}>−</button>
                          <span style={{ padding: '4px 12px', fontWeight: 600, fontSize: 'var(--font-size-sm)', minWidth: '32px', textAlign: 'center' }}>{installmentCount}</span>
                          <button type="button" onClick={() => setInstallmentCount(c => Math.min(24, c + 1))} className="btn btn-secondary" style={{ border: 'none', borderRadius: 0, padding: '4px 10px', fontSize: '16px', lineHeight: 1 }}>+</button>
                        </div>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>installments</span>
                        <button
                          onClick={() => {
                            const s = splitEqually(installmentCount, form.invoiceDate);
                            const lastDate = s[s.length - 1].dueDate;
                            setForm(f => ({ ...f, paymentSchedule: s, terms: 'As per Payment Schedule', dueDate: lastDate }));
                          }}
                          className="btn btn-primary btn-sm"
                          style={{ fontSize: '11px', padding: '2px 14px' }}
                        >
                          Split Equally
                        </button>
                      </div>
                    ) : (
                      <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 'var(--spacing-sm)' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                              <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>Installment</th>
                              <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '80px' }}>%</th>
                              <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '120px' }}>Amount</th>
                              <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '160px' }}>Due Date</th>
                              <th style={{ width: '40px' }} />
                            </tr>
                          </thead>
                          <tbody>
                            {form.paymentSchedule.map((ps, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                                <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                                  <input className="form-input" value={ps.label} onChange={e => {
                                    const s = [...form.paymentSchedule]; s[i] = { ...s[i], label: e.target.value }; set('paymentSchedule', s);
                                  }} placeholder={`Installment ${i + 1}`} />
                                </td>
                                <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                                  <input className="form-input no-spinner" type="number" min="0" max="100" value={ps.percent} onChange={e => {
                                    const s = [...form.paymentSchedule]; s[i] = { ...s[i], percent: e.target.value, amountOverride: undefined }; set('paymentSchedule', s);
                                  }} style={{ textAlign: 'right' }} />
                                </td>
                                <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                                  <input className="form-input no-spinner" type="number" min="0"
                                    value={ps.amountOverride !== undefined ? ps.amountOverride : Math.round(grandTotal * (parseFloat(ps.percent) || 0) / 100 * 100) / 100}
                                    onChange={e => {
                                      const pct = grandTotal > 0 ? Math.round((parseFloat(e.target.value) || 0) / grandTotal * 10000) / 100 : 0;
                                      const s = [...form.paymentSchedule]; s[i] = { ...s[i], percent: pct, amountOverride: e.target.value }; set('paymentSchedule', s);
                                    }}
                                    onBlur={e => {
                                      const s = [...form.paymentSchedule]; s[i] = { ...s[i], amountOverride: undefined }; set('paymentSchedule', s);
                                    }}
                                    style={{ textAlign: 'right' }} />
                                </td>
                                <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                                  <input type="date" className="form-input" value={ps.dueDate} onChange={e => {
                                    const s = [...form.paymentSchedule];
                                    s[i] = recordDueDateChange(s[i], e.target.value, !!editingInvoiceId);
                                    // Rescheduling one installment pushes each following unpaid
                                    // installment one month later, keeping its own day-of-month.
                                    // Only the manually changed one is marked as postponed.
                                    if (e.target.value) {
                                      for (let j = i + 1; j < s.length; j++) {
                                        if (s[j].paid || s[j].waived || !s[j].dueDate) continue;
                                        s[j] = { ...s[j], dueDate: addMonths(s[j].dueDate, 1) };
                                      }
                                    }
                                    const lastDate = s.reduce((max, p) => p.dueDate > max ? p.dueDate : max, s[0].dueDate);
                                    setForm(f => ({ ...f, paymentSchedule: s, dueDate: lastDate }));
                                  }} />
                                  <PostponementNote obj={ps} onClear={() => {
                                    const s = [...form.paymentSchedule];
                                    const { originalDueDate, dueDateHistory, ...rest } = s[i];
                                    s[i] = rest;
                                    set('paymentSchedule', s);
                                  }} />
                                </td>
                                <td style={{ padding: 'var(--spacing-xs)', textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                                    <button onClick={() => {
                                      const s = form.paymentSchedule.filter((_, j) => j !== i);
                                      if (s.length === 0) {
                                        setForm(f => ({ ...f, paymentSchedule: [], terms: 'Due on Receipt', dueDate: f.invoiceDate }));
                                      } else {
                                        const lastDate = s.reduce((max, p) => p.dueDate > max ? p.dueDate : max, s[0].dueDate);
                                        setForm(f => ({ ...f, paymentSchedule: s, dueDate: lastDate }));
                                      }
                                    }} className="btn btn-secondary btn-sm" style={{ padding: '4px 8px', fontSize: '14px', lineHeight: 1 }}>&times;</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
                            <button onClick={() => {
                              const newDate = addDays(form.invoiceDate, 30 * form.paymentSchedule.length);
                              const s = [...form.paymentSchedule, { percent: '', dueDate: newDate, label: `Installment ${form.paymentSchedule.length + 1}`, paid: false }];
                              const lastDate = s.reduce((max, p) => p.dueDate > max ? p.dueDate : max, s[0].dueDate);
                              setForm(f => ({ ...f, paymentSchedule: s, dueDate: lastDate }));
                            }} className="btn btn-secondary btn-sm">+ Add Installment</button>
                            <button onClick={() => {
                              const s = splitEqually(form.paymentSchedule.length, form.invoiceDate);
                              const lastDate = s[s.length - 1].dueDate;
                              setForm(f => ({ ...f, paymentSchedule: s, dueDate: lastDate }));
                            }} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }}>Re-split Equally</button>
                          </div>
                          {(() => {
                            const totalPct = form.paymentSchedule.reduce((s, p) => s + (parseFloat(p.percent) || 0), 0);
                            return totalPct !== 100 ? (
                              <span style={{ fontSize: 'var(--font-size-xs)', color: '#DC2626', fontWeight: 'var(--font-weight-medium)' }}>
                                Total: {totalPct}% (must be 100%)
                              </span>
                            ) : (
                              <span style={{ fontSize: 'var(--font-size-xs)', color: '#065F46', fontWeight: 'var(--font-weight-medium)' }}>
                                Total: 100%
                              </span>
                            );
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Account Details */}
              <div className="card mb-md">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="card-header" style={{ borderBottom: editingAccount ? undefined : 'none', marginBottom: editingAccount ? undefined : 0, paddingBottom: editingAccount ? undefined : 0 }}>Account Details</div>
                  <button onClick={() => setEditingAccount(e => !e)} className="btn btn-secondary btn-sm" style={{ padding: '3px 10px', fontSize: '12px' }}>{editingAccount ? 'Done' : 'Edit'}</button>
                </div>
                {editingAccount ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                    <div className="form-group"><label className="form-label">Account Name</label><input className="form-input" value={form.accountName} onChange={e => set('accountName', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Account Number</label><input className="form-input" value={form.accountNumber} onChange={e => set('accountNumber', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Bank</label><input className="form-input" value={form.bankName} onChange={e => set('bankName', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">IFSC</label><input className="form-input" value={form.ifsc} onChange={e => set('ifsc', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Branch</label><input className="form-input" value={form.branch} onChange={e => set('branch', e.target.value)} /></div>
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.8, marginTop: 'var(--spacing-sm)' }}>
                    {form.accountName && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Account Name:</span> {form.accountName}</div>}
                    {form.accountNumber && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Account No:</span> {form.accountNumber}</div>}
                    {form.bankName && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Bank:</span> {form.bankName}</div>}
                    {form.ifsc && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>IFSC:</span> {form.ifsc}</div>}
                    {form.branch && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Branch:</span> {form.branch}</div>}
                  </div>
                )}
              </div>

              {/* Validation + Actions */}
              {!canDownload && (
                <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  <span style={{ fontSize: '16px' }}>!</span>
                  <span>
                    {!form.billTo.trim() && <><strong>Bill To</strong> is required. </>}
                    {!form.items.some(it => it.description.trim() && (parseFloat(it.rate) || 0) > 0) && <><strong>At least one item</strong> with description and rate is required.</>}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
                {(editingInvoiceId || duplicateMode) ? (
                  <>
                    <button onClick={() => { setEditingInvoiceId(null); setDuplicateMode(false); }} className="btn btn-secondary">Cancel</button>
                    <button onClick={saveInvoice} disabled={!canDownload || saving} className="btn btn-primary">
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={async () => {
                      sessionStorage.removeItem('invoiceForm');
                      const f = defaultForm();
                      try {
                        const res = await salesInvoiceService.getNextNumber();
                        f.invoiceNumber = res.invoiceNumber;
                      } catch { f.invoiceNumber = makeInvNo(getFY(), 1); }
                      setForm(f);
                    }} className="btn btn-secondary">Clear</button>
                    <button onClick={() => setShowPreview(true)} disabled={!canDownload} className="btn btn-primary">Preview</button>
                  </>
                )}
              </div>
            </>
          )}

          </>}
        </div>
      </div>

      {/* Hidden preview for PDF generation (used when preview is not shown) */}
      {!showPreview && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <InvoicePreview
            ref={previewRef}
            form={form}
            subtotal={subtotal}
            discount={discount}
            totalCgst={totalCgst}
            totalSgst={totalSgst}
            totalIgst={totalIgst}
            grandTotal={grandTotal}
            itemSubtotal={itemSubtotal}
            itemGst={itemGst}
            itemTotal={itemTotal}
          />
        </div>
      )}

      {/* Hidden preview for downloading saved invoices */}
      {dlInvoice && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <InvoicePreview
            ref={dlPreviewRef}
            form={dlInvoice.formData}
            subtotal={parseFloat(dlInvoice.subtotal)}
            discount={parseFloat(dlInvoice.formData?.discount) || 0}
            totalCgst={parseFloat(dlInvoice.totalCgst)}
            totalSgst={parseFloat(dlInvoice.totalSgst)}
            totalIgst={parseFloat(dlInvoice.totalIgst)}
            grandTotal={parseFloat(dlInvoice.grandTotal)}
            itemSubtotal={itemSubtotal}
            itemGst={itemGst}
            itemTotal={itemTotal}
          />
        </div>
      )}

      <Footer />
    </>
  );
};

export default GenerateInvoice;
